import React, { FC, useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  Switch,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  gray40,
  gray100,
  gray80,
  white,
  orange50,
  yellow50,
  yellow_overlay10,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import {
  boldMd,
  regularXl,
  regularLg,
  regularMd,
  regularSm,
  regularXs,
} from '../../../constants/location';
import {
  exportFileType,
  exportEncoding,
  fixedFieldIdMap,
  FIXED_FIELD_ID_PREFIX,
  CHILD_FIELD_ID_SEPARATOR,
  EXPORT_HEADER_MAX_LENGTH,
  exportQuoteMode,
} from '../../../constants/enum';
import { type ExportTemplate } from '../../../types/exportTemplate';
import { PatternType } from '../../../types/pattern';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import CustomTextfield from '../form/CustomTextfield';
import FileNameWithExtensionField from '../form/FileNameWithExtensionField';
import { generateFileName } from '../../../utils/pattern/fileExportUtils';
import { validateFileName } from '../../../utils/fileNameValidation';

// 固定項目のID一覧
const allFixedFieldIds = Object.keys(fixedFieldIdMap);

// 統合リストの各アイテムの型
// #3159: 固定項目・読取項目（親）・子項目を同一階層で並び替えるため、子項目も要素として持つ
type UnifiedSortItem =
  | { type: 'fixed'; id: string; label: string }
  | { type: 'parent'; id: string; item: PatternType['item_list'][0] }
  | {
      type: 'child';
      id: string;
      parentId: string;
      parentValue: string;
      child: PatternType['item_list'][0]['children'][0];
    };

// 保存された並び順でitem_listをソートする
const sortItemListByOrder = (
  itemList: PatternType['item_list'],
  itemOrder: string[],
  childOrder?: Record<string, string[]>,
): PatternType['item_list'] => {
  const sorted = [...itemList].sort((a, b) => {
    const indexA = itemOrder.indexOf(a.id);
    const indexB = itemOrder.indexOf(b.id);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  if (childOrder) {
    return sorted.map(item => {
      const order = childOrder[item.id];
      if (order && item.children?.length > 0) {
        const sortedChildren = [...item.children].sort((a, b) => {
          const idxA = order.indexOf(a.id);
          const idxB = order.indexOf(b.id);
          if (idxA === -1 && idxB === -1) return 0;
          if (idxA === -1) return 1;
          if (idxB === -1) return -1;
          return idxA - idxB;
        });
        return { ...item, children: sortedChildren };
      }
      return item;
    });
  }
  return sorted;
};

// 各項目の既定の列名（カスタムの列名が未入力のときに出力される名称）
// 子項目は親の名称を含めず、項目名をそのまま列名にする
const getDefaultLabel = (item: UnifiedSortItem): string => {
  if (item.type === 'fixed') return item.label;
  if (item.type === 'child') return item.child.childValue;
  return item.item.parentValue;
};

// ソート可能な出力項目の行（固定項目・読取項目・子項目で共通）
// #3159: すべての項目を同一階層で並び替えるため、行の描画を1つに統合している
// #3158: 各行で出力する列名を編集できる（未入力時は元の項目名で出力する）
const SortableExportItem: FC<{
  id: string;
  label: string;
  checked: boolean;
  customLabel: string;
  defaultLabel: string;
  errorText?: string;
  warningText?: string;
  onToggle: (fieldId: string) => void;
  onCustomLabelChange: (fieldId: string, value: string) => void;
}> = ({
  id,
  label,
  checked,
  customLabel,
  defaultLabel,
  errorText,
  warningText,
  onToggle,
  onCustomLabelChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 0',
        border: isDragging ? '2px dashed #ccc' : 'none',
        borderRadius: '4px',
        backgroundColor: isDragging ? 'rgba(0,0,0,0.05)' : 'transparent',
      }}
    >
      {/* ドラッグハンドル */}
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        sx={{
          padding: '4px',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: gray80 }} />
      </IconButton>

      {/* 出力対象のチェックボックス */}
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(): void => onToggle(id)}
            sx={{ '&.Mui-checked': { color: orange50 }, padding: '4px 8px' }}
          />
        }
        label={label}
        sx={{
          margin: 0,
          alignItems: 'center',
          minHeight: '40px',
          flex: 1,
          '& .MuiFormControlLabel-label': {
            lineHeight: '1.2',
          },
        }}
      />

      {/* 出力する列名（未入力時は元の項目名で出力する） */}
      <Box sx={{ width: '240px', flexShrink: 0 }}>
        <CustomTextfield
          value={customLabel}
          onChange={(e): void => onCustomLabelChange(id, e.target.value)}
          placeholder={defaultLabel}
          disabled={!checked}
          error={Boolean(errorText)}
          helperText={errorText}
          inputWidth="100%"
          height="36px"
          formMb="0"
          fontSizeIs14px
          inputPaddingIs10px8px
        />
        {/* 列名の重複は出力を止めず、警告として知らせる（取込側での扱いは利用者の判断に委ねる） */}
        {!errorText && warningText && (
          <Typography sx={{ ...regularXs, color: yellow50, marginTop: '2px' }}>
            {warningText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '60%',
      maxWidth: '800px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  // 未保存の編集があるときの注意書き（他の警告表示と同じ配色）
  unsavedAlert: {
    ...regularMd,
    backgroundColor: yellow_overlay10,
    border: `1px solid ${yellow50}`,
    borderRadius: '4px',
    color: yellow50,
    padding: '8px 12px',
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  uploadCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  uploadCancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  uploadSubmitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  uploadSubmitButtonText: {
    ...boldMd,
    color: white,
  },
  radioGroupRow: {
    flexDirection: 'row',
  },
  radioOrange: {
    '&.Mui-checked': {
      color: orange50,
    },
  },
};

/** 出力形式ラジオで「指定フォーマット」を表す値（exportFileType とは別系統） */
const TEMPLATE_MODE_VALUE = 'template';

const FileExportDialog: FC<{
  open: boolean;
  onClose: () => void;
  onExport: (
    fileType: (typeof exportFileType)[keyof typeof exportFileType],
    encoding: (typeof exportEncoding)[keyof typeof exportEncoding],
    selectedFields: string[],
    sortedPattern?: PatternType,
    moveToOutputted?: boolean,
    saveSetting?: boolean,
    selectedFixedFields?: string[],
    sortedUnifiedOrder?: string[],
    customFileName?: string,
    customHeaders?: Record<string, string>,
    quoteMode?: (typeof exportQuoteMode)[keyof typeof exportQuoteMode],
  ) => Promise<void>;
  pattern: PatternType | null;
  selectedTab: number;
  defaultFileName?: string;
  /** チェック行のみ出力（#2803）: 一覧で選択中の件数。0 のとき全件出力 */
  selectedCount?: number;
  /**
   * 指定フォーマット出力（#3070）。1件出力の画面でのみ渡す。
   * 未指定なら選択肢自体を出さない（複数件出力は「1件=1ファイル」のため対象外）。
   */
  exportTemplates?: ExportTemplate[];
  /**
   * 指定フォーマット出力を実行する。
   * ファイル名・出力後の処理は通常出力と同じ設定を使う。
   */
  onTemplateExport?: (
    template: ExportTemplate,
    customFileName?: string,
    moveToOutputted?: boolean,
  ) => Promise<void>;
  /**
   * 画面上に保存されていない編集があるか。
   * 出力は保存済みの内容を使うため、編集中の値は反映されない旨を知らせる。
   */
  hasUnsavedEdits?: boolean;
}> = ({
  open,
  onClose,
  onExport,
  pattern,
  selectedTab,
  defaultFileName,
  selectedCount = 0,
  exportTemplates = [],
  onTemplateExport,
  hasUnsavedEdits = false,
}) => {
  const proxyDialogSx = useProxyDialogSx();
  const [fileType, setFileType] = useState<(typeof exportFileType)[keyof typeof exportFileType]>(
    exportFileType.csv,
  );
  const [encoding, setEncoding] = useState<(typeof exportEncoding)[keyof typeof exportEncoding]>(
    exportEncoding.utf8,
  );
  // CSV出力時の引用符の付け方（#3160）
  const [quoteMode, setQuoteMode] = useState<
    (typeof exportQuoteMode)[keyof typeof exportQuoteMode]
  >(exportQuoteMode.minimal);
  const [selectedExportFields, setSelectedExportFields] = useState<string[]>([]);
  // 指定フォーマット出力（#3070）で選ばれたフォーマットのID
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  /**
   * 画面上の出力形式の選択（#3070）。
   * 指定フォーマットは生成経路が別（サーバ側）で、既存の出力処理は excel/csv 前提のため、
   * exportFileType は拡張せずここで区別する。
   */
  const [isTemplateMode, setIsTemplateMode] = useState<boolean>(false);
  /** 指定フォーマット出力を選べるか（1件出力の画面で登録済みのときだけ） */
  const canUseTemplate = exportTemplates.length > 0 && Boolean(onTemplateExport);
  const [selectedFixedFields, setSelectedFixedFields] = useState<string[]>(allFixedFieldIds);
  const [unifiedList, setUnifiedList] = useState<UnifiedSortItem[]>([]);
  // 出力する列名のカスタマイズ（#3158）。キーは統合リストのID
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>({});
  const [moveToOutputted, setMoveToOutputted] = useState<boolean>(true);
  const [saveSetting, setSaveSetting] = useState<boolean>(true);
  const [customFileName, setCustomFileName] = useState<string>('');
  const [customFileNameTouched, setCustomFileNameTouched] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  // 全選択可能な項目を計算
  const getAllSelectableFields = (): string[] => {
    if (!pattern) return [];

    const allFields: string[] = [];
    pattern.item_list.forEach(item => {
      // 親項目を追加
      allFields.push(item.id.toString());
      // 子項目があれば追加
      if (item.children && item.children.length > 0) {
        item.children.forEach(child => {
          allFields.push(`${item.id}-${child.id}`);
        });
      }
    });
    return allFields;
  };

  // 統合リストを構築するヘルパー
  // #3159: 子項目も同一階層の要素として展開し、読取項目と混ぜて並び替えられるようにする
  const buildUnifiedList = (
    patternItemList: PatternType['item_list'],
    savedOrder?: string[],
  ): UnifiedSortItem[] => {
    const fixedItems: UnifiedSortItem[] = Object.entries(fixedFieldIdMap).map(([id, label]) => ({
      type: 'fixed' as const,
      id,
      label,
    }));

    // 読取項目は「親 → その子」の順に展開する（保存順がない場合の既定の並び）
    const patternItems: UnifiedSortItem[] = [];
    patternItemList.forEach(item => {
      patternItems.push({ type: 'parent' as const, id: item.id.toString(), item });
      item.children?.forEach(child => {
        patternItems.push({
          type: 'child' as const,
          id: `${item.id}${CHILD_FIELD_ID_SEPARATOR}${child.id}`,
          parentId: item.id.toString(),
          parentValue: item.parentValue,
          child,
        });
      });
    });

    const allItems = [...fixedItems, ...patternItems];

    if (!savedOrder || !savedOrder.some(id => id.startsWith(FIXED_FIELD_ID_PREFIX))) {
      // レガシー形式 or 未保存: 基本項目 → 読取項目（保存順）
      return allItems;
    }

    // 保存された統合リスト形式: 並び順に従ってソート
    const itemById = new Map(allItems.map(item => [item.id, item]));
    const ordered: UnifiedSortItem[] = [];
    const usedIds = new Set<string>();

    savedOrder.forEach(orderId => {
      const item = itemById.get(orderId);
      if (!item || usedIds.has(orderId)) return;
      ordered.push(item);
      usedIds.add(orderId);

      // 子項目IDを持たない保存データ（#3159 以前）は、親の直後に子を並べて従来の順序を保つ
      if (item.type === 'parent') {
        allItems.forEach(candidate => {
          if (
            candidate.type === 'child' &&
            candidate.parentId === item.id &&
            !savedOrder.includes(candidate.id) &&
            !usedIds.has(candidate.id)
          ) {
            ordered.push(candidate);
            usedIds.add(candidate.id);
          }
        });
      }
    });

    // 保存後に追加された項目は末尾へ回す
    allItems.forEach(item => {
      if (!usedIds.has(item.id)) ordered.push(item);
    });

    return ordered;
  };

  // モーダルが開かれたとき、またはパターンが変わったときに保存設定を復元
  // （pattern を持たないフリーOCR等の呼び出し元向け）
  /**
   * 指定フォーマット（#3070）の既定選択。
   * 登録されている場合は、書式付きで出すのが本来の使い方のため既定にする。
   * フォーマットも1件目を選んでおき、そのまま出力できるようにする。
   */
  useEffect(() => {
    if (!open) return;
    if (exportTemplates.length > 0 && onTemplateExport) {
      setIsTemplateMode(true);
      setSelectedTemplateId(prev =>
        // 既に選んでいるものが残っていればそれを尊重する
        prev && exportTemplates.some(t => t.id === prev) ? prev : (exportTemplates[0].id ?? ''),
      );
    } else {
      setIsTemplateMode(false);
    }
  }, [open, exportTemplates, onTemplateExport]);

  /**
   * 指定フォーマットのファイル名の既定値（#3070）。
   * 他の出力と同じく、フォーマット名の末尾に出力日時を付ける。
   * 利用者が編集したあとは上書きしない。
   */
  useEffect(() => {
    if (!open || !isTemplateMode || customFileNameTouched) return;
    const template = exportTemplates.find(t => t.id === selectedTemplateId);
    if (template) {
      setCustomFileName(`${template.template_name}_${dayjs().format('YYYYMMDDHHmmss')}`);
    }
  }, [open, isTemplateMode, selectedTemplateId, exportTemplates, customFileNameTouched]);

  /**
   * 指定フォーマットから CSV/Excel へ戻したときにファイル名を元に戻す（#3207）。
   *
   * 指定フォーマットを選ぶとファイル名を「フォーマット名_日時」で上書きするため、
   * 戻したときに明示的に組み立て直さないとフォーマット名が残ってしまう。
   * 通常出力のファイル名を決める effect は isTemplateMode に依存しておらず再実行されない。
   * 利用者が自分で編集していた場合は、既存の考え方どおり上書きしない。
   */
  useEffect(() => {
    if (!open || isTemplateMode || customFileNameTouched) return;

    const base = pattern
      ? generateFileName(
          pattern,
          selectedTab,
          pattern.export_settings?.file_type ?? fileType,
          pattern.export_settings?.encoding ?? encoding,
        ).replace(/\.(csv|xlsx)$/, '')
      : defaultFileName;
    if (!base) return;

    // CSV は自動生成名の末尾にエンコーディングを付ける（上の effect と揃える）
    setCustomFileName(
      fileType === exportFileType.csv && !pattern
        ? `${base}${encoding === exportEncoding.shiftJis ? '_sjis' : '_utf8'}`
        : base,
    );
  }, [
    open,
    isTemplateMode,
    customFileNameTouched,
    pattern,
    selectedTab,
    fileType,
    encoding,
    defaultFileName,
  ]);

  useEffect(() => {
    if (!open || pattern) return;
    if (defaultFileName) {
      // CSV 出力時は自動生成名の末尾に現在のエンコーディング（_utf8 / _sjis）を付与する。
      // ユーザーが末尾を消した場合はそのまま尊重される（エンコ切替時の置換は末尾一致時のみ動作）。
      const withSuffix =
        fileType === exportFileType.csv
          ? `${defaultFileName}${encoding === exportEncoding.shiftJis ? '_sjis' : '_utf8'}`
          : defaultFileName;
      setCustomFileName(withSuffix);
      setCustomFileNameTouched(false);
      setExporting(false);
    }
  }, [open, pattern, defaultFileName, fileType, encoding]);

  useEffect(() => {
    if (!open || !pattern) return;

    const settings = pattern.export_settings;
    // 保存済み設定があればそれを優先してファイル名の初期値を決定（state更新の非同期性によるズレを防ぐ）
    const effectiveFileType = settings?.file_type ?? fileType;
    const effectiveEncoding = settings?.encoding ?? encoding;

    // ファイル名は自動生成名を初期値として設定（拡張子は別途付与されるため除く）
    const defaultFileName = generateFileName(
      pattern,
      selectedTab,
      effectiveFileType,
      effectiveEncoding,
    ).replace(/\.(csv|xlsx)$/, '');
    setCustomFileName(defaultFileName);
    setCustomFileNameTouched(false);
    setExporting(false);

    if (settings) {
      setFileType(settings.file_type);
      setEncoding(settings.encoding);
      setQuoteMode(settings.quote_mode ?? exportQuoteMode.minimal);
      setMoveToOutputted(settings.move_to_outputted);

      // 固定項目の復元（日本語名 → fixed_xxx IDに変換）
      const restoredFixedFields = settings.selected_fixed_fields
        ? Object.entries(fixedFieldIdMap)
            .filter(([, label]) => settings.selected_fixed_fields?.includes(label))
            .map(([id]) => id)
        : allFixedFieldIds;
      setSelectedFixedFields(restoredFixedFields);

      const allFields = getAllSelectableFields();
      const validFields = settings.selected_fields.filter(f => allFields.includes(f));
      setSelectedExportFields(validFields.length > 0 ? validFields : allFields);

      // 子項目の並び順を復元してから統合リストを構築
      const sortedPatternList = sortItemListByOrder(
        pattern.item_list,
        settings.sorted_item_order,
        settings.sorted_child_order,
      );
      setUnifiedList(buildUnifiedList(sortedPatternList, settings.sorted_item_order));
      setCustomHeaders(settings.custom_headers ?? {});
    } else {
      setSelectedExportFields(getAllSelectableFields());
      setSelectedFixedFields(allFixedFieldIds);
      setUnifiedList(buildUnifiedList(pattern.item_list));
      setCustomHeaders({});
      setMoveToOutputted(true);
    }
  }, [open, pattern]);

  // エクスポートフィールドの選択処理
  // #3159: 親項目と子項目は独立して選択できる（配列のインデックス列だけを外すこともできる）
  const handleExportFieldToggle = (fieldId: string): void => {
    setSelectedExportFields(prev =>
      prev.includes(fieldId) ? prev.filter(id => id !== fieldId) : [...prev, fieldId],
    );
  };

  // 出力する列名の入力処理（#3158）
  const handleCustomLabelChange = (fieldId: string, value: string): void => {
    setCustomHeaders(prev => ({ ...prev, [fieldId]: value }));
  };

  // 固定項目のトグル
  const handleFixedFieldToggle = (fieldId: string): void => {
    setSelectedFixedFields(prev =>
      prev.includes(fieldId) ? prev.filter(f => f !== fieldId) : [...prev, fieldId],
    );
  };

  // 全選択/全解除（基本項目+読取項目）
  const handleSelectAll = (): void => {
    const allPatternFields = getAllSelectableFields();
    const isAllSelected =
      selectedExportFields.length === allPatternFields.length &&
      selectedFixedFields.length === allFixedFieldIds.length;

    if (isAllSelected) {
      setSelectedExportFields([]);
      setSelectedFixedFields([]);
    } else {
      setSelectedExportFields(allPatternFields);
      setSelectedFixedFields(allFixedFieldIds);
    }
  };

  // ドラッグ&ドロップのセンサー設定
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // 統合リストのドラッグ終了処理
  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setUnifiedList(items => {
        const oldIndex = items.findIndex(item => item.id === active.id.toString());
        const newIndex = items.findIndex(item => item.id === over.id.toString());
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // 出力する列名のバリデーション（#3158）
  // 文字数超過は出力を止めるエラー、列名の重複は出力を止めない警告として扱う
  const { customLabelErrors, customLabelWarnings } = useMemo(() => {
    const isSelected = (item: UnifiedSortItem): boolean =>
      item.type === 'fixed'
        ? selectedFixedFields.includes(item.id)
        : selectedExportFields.includes(item.id);
    const resolveLabel = (item: UnifiedSortItem): string =>
      (customHeaders[item.id] ?? '').trim() || getDefaultLabel(item);

    const labelCounts = new Map<string, number>();
    unifiedList.forEach(item => {
      if (!isSelected(item)) return;
      const label = resolveLabel(item);
      labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    });

    const errors: Record<string, string> = {};
    const warnings: Record<string, string> = {};
    unifiedList.forEach(item => {
      if (!isSelected(item)) return;
      if ((customHeaders[item.id] ?? '').trim().length > EXPORT_HEADER_MAX_LENGTH) {
        errors[item.id] = `${EXPORT_HEADER_MAX_LENGTH}文字以内で入力してください`;
        return;
      }
      if ((labelCounts.get(resolveLabel(item)) ?? 0) > 1) {
        warnings[item.id] = '他の項目と列名が重複しています';
      }
    });

    return { customLabelErrors: errors, customLabelWarnings: warnings };
  }, [unifiedList, customHeaders, selectedExportFields, selectedFixedFields]);

  const hasCustomLabelError = Object.keys(customLabelErrors).length > 0;

  // ファイル名バリデーション（必須）
  const customFileNameError = validateFileName(customFileName);

  /** 指定フォーマット出力（#3070）を実行する。通常出力とは経路が別 */
  const handleTemplateExport = async (): Promise<void> => {
    const template = exportTemplates.find(t => t.id === selectedTemplateId);
    if (!template || !onTemplateExport || exporting) return;
    setExporting(true);
    try {
      await onTemplateExport(template, customFileName.trim() || undefined, moveToOutputted);
      onClose();
    } catch {
      // 親側でエラー通知済み。リトライできるようモーダルは開いたままにする
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async (): Promise<void> => {
    if (customFileNameError || hasCustomLabelError || exporting) return;

    // unifiedListから読取項目（親）だけ抽出してsortedPatternを作成
    // #3159: 列の並び順は sortedUnifiedOrder が正だが、item_list 側の children も
    // 統合リスト上の順序に合わせておき、保存設定を読み直したときの並びを実際の表示順と揃える
    const childIdOrderByParent = new Map<string, string[]>();
    unifiedList.forEach(item => {
      if (item.type !== 'child') return;
      const childIds = childIdOrderByParent.get(item.parentId) ?? [];
      childIds.push(item.child.id);
      childIdOrderByParent.set(item.parentId, childIds);
    });

    const sortedPatternItems = unifiedList
      .filter(
        (item): item is Extract<UnifiedSortItem, { type: 'parent' }> => item.type === 'parent',
      )
      .map(item => {
        const childIdOrder = childIdOrderByParent.get(item.id);
        if (!childIdOrder || !item.item.children?.length) return item.item;
        const sortedChildren = [...item.item.children].sort(
          (a, b) => childIdOrder.indexOf(a.id) - childIdOrder.indexOf(b.id),
        );
        return { ...item.item, children: sortedChildren };
      });
    const sortedPattern = pattern ? { ...pattern, item_list: sortedPatternItems } : undefined;

    // selectedFixedFields を fixed_xxx ID から日本語名に変換
    const fixedFieldNames = selectedFixedFields.map(id => fixedFieldIdMap[id]).filter(Boolean);

    // 統合リストの並び順
    const sortedUnifiedOrder = unifiedList.map(item => item.id);

    // 未入力の列名は保存しない（元の項目名で出力する）
    // 読取項目の削除などで存在しなくなった項目の設定も残さない
    const existingFieldIds = new Set(unifiedList.map(item => item.id));
    const trimmedCustomHeaders = Object.fromEntries(
      Object.entries(customHeaders)
        .filter(([fieldId]) => existingFieldIds.has(fieldId))
        .map(([fieldId, label]) => [fieldId, label.trim()])
        .filter(([, label]) => label !== ''),
    );

    const trimmedCustomFileName = customFileName.trim();

    setExporting(true);
    try {
      await onExport(
        fileType,
        encoding,
        selectedExportFields,
        sortedPattern,
        moveToOutputted,
        saveSetting,
        fixedFieldNames,
        sortedUnifiedOrder,
        trimmedCustomFileName || undefined,
        trimmedCustomHeaders,
        quoteMode,
      );
      onClose();
    } catch {
      // 親側でエラー通知済み。モーダルを開いたまま残してリトライできるようにする。
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>ファイル出力</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <CloseIcon sx={styles.dialogCloseButton} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* 出力は保存済みの内容を使うため、編集中の値が混ざらないよう知らせる */}
        {hasUnsavedEdits && (
          <Typography sx={styles.unsavedAlert}>
            保存されていない編集があります。保存してから出力してください
          </Typography>
        )}

        {/* チェック行のみ出力（#2803）: 選択中のときのみ対象件数を表示 */}
        {selectedCount > 0 && (
          <Typography sx={{ ...regularMd, color: gray80 }}>
            対象: 選択中{' '}
            <Box component="span" sx={{ fontWeight: 700, color: orange50 }}>
              {selectedCount}
            </Box>{' '}
            件
          </Typography>
        )}

        {/* 出力形式 */}
        <FormControl component="fieldset">
          <Typography
            sx={{
              mb: 1,
              color: gray100,
              paddingLeft: '12px',
              borderLeft: `4px solid ${orange50}`,
            }}
          >
            出力形式
          </Typography>
          <RadioGroup
            row
            name="fileType"
            value={isTemplateMode ? TEMPLATE_MODE_VALUE : fileType}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              if (e.target.value === TEMPLATE_MODE_VALUE) {
                // 指定フォーマットは文字コードや出力項目の設定を使わないため、
                // fileType は変えずにモードだけ切り替える
                setIsTemplateMode(true);
                return;
              }
              setIsTemplateMode(false);
              const newFileType = e.target
                .value as (typeof exportFileType)[keyof typeof exportFileType];
              setFileType(newFileType);
              if (newFileType === exportFileType.excel) {
                // Excelに切り替えた場合は末尾のCSVエンコーディングサフィックスを削除
                setCustomFileName(prev => prev.replace(/_(utf8|sjis)$/, ''));
              } else {
                // CSVに切り替えた場合は末尾にサフィックスがなければ現在のエンコーディングのものを付与
                const newSuffix = encoding === exportEncoding.shiftJis ? '_sjis' : '_utf8';
                setCustomFileName(prev =>
                  /_(utf8|sjis)$/.test(prev) ? prev : `${prev}${newSuffix}`,
                );
              }
            }}
            sx={styles.radioGroupRow}
          >
            <FormControlLabel
              value={exportFileType.excel}
              control={<Radio sx={styles.radioOrange} />}
              label="エクセル(.xlsx)"
            />
            <FormControlLabel
              value={exportFileType.csv}
              control={<Radio sx={styles.radioOrange} />}
              label="CSV"
            />
            {canUseTemplate && (
              <FormControlLabel
                value={TEMPLATE_MODE_VALUE}
                control={<Radio sx={styles.radioOrange} />}
                label="指定フォーマット"
              />
            )}
          </RadioGroup>
        </FormControl>

        {/* 使用するフォーマットの選択（#3070）。出力形式で「指定フォーマット」を選んだときだけ出す */}
        {isTemplateMode && canUseTemplate && (
          <FormControl component="fieldset">
            <Typography
              sx={{
                mb: 1,
                color: gray100,
                paddingLeft: '12px',
                borderLeft: `4px solid ${orange50}`,
              }}
            >
              使用するフォーマット
            </Typography>
            <RadioGroup
              name="exportTemplate"
              value={selectedTemplateId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setSelectedTemplateId(e.target.value)
              }
            >
              {exportTemplates.map(t => (
                <FormControlLabel
                  key={t.id}
                  value={t.id}
                  control={<Radio sx={styles.radioOrange} />}
                  label={t.template_name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}

        {/* CSV文字コード（CSV選択時のみ表示。指定フォーマットでは使わない） */}
        {!isTemplateMode && fileType === exportFileType.csv && (
          <FormControl component="fieldset">
            <Typography
              sx={{
                mb: 1,
                color: gray100,
                paddingLeft: '12px',
                borderLeft: `4px solid ${orange50}`,
              }}
            >
              CSV文字コード
            </Typography>
            <RadioGroup
              row
              name="encoding"
              value={encoding}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                const newEncoding = e.target
                  .value as (typeof exportEncoding)[keyof typeof exportEncoding];
                setEncoding(newEncoding);
                const newSuffix = newEncoding === exportEncoding.shiftJis ? '_sjis' : '_utf8';
                setCustomFileName(prev => prev.replace(/_(utf8|sjis)$/, newSuffix));
              }}
              sx={styles.radioGroupRow}
            >
              <FormControlLabel
                value={exportEncoding.utf8}
                control={<Radio sx={styles.radioOrange} />}
                label="UTF-8(推奨)"
              />
              <FormControlLabel
                value={exportEncoding.shiftJis}
                control={<Radio sx={styles.radioOrange} />}
                label="Shift-JIS"
              />
            </RadioGroup>
          </FormControl>
        )}

        {/* CSVの引用符（CSV選択時のみ表示・#3160。指定フォーマットでは使わない） */}
        {!isTemplateMode && fileType === exportFileType.csv && (
          <FormControl component="fieldset">
            <Typography
              sx={{
                mb: 1,
                color: gray100,
                paddingLeft: '12px',
                borderLeft: `4px solid ${orange50}`,
              }}
            >
              引用符
            </Typography>
            <RadioGroup
              row
              name="quoteMode"
              value={quoteMode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setQuoteMode(
                  e.target.value as (typeof exportQuoteMode)[keyof typeof exportQuoteMode],
                )
              }
              sx={styles.radioGroupRow}
            >
              <FormControlLabel
                value={exportQuoteMode.all}
                control={<Radio sx={styles.radioOrange} />}
                label="あり"
              />
              <FormControlLabel
                value={exportQuoteMode.minimal}
                control={<Radio sx={styles.radioOrange} />}
                label="なし"
              />
            </RadioGroup>
            <Typography sx={{ ...regularSm, color: gray80 }}>
              「なし」の場合も、カンマや改行を含む値は引用符で囲まれます
            </Typography>
          </FormControl>
        )}

        {/* 出力項目選択（指定フォーマットは書式側で決まるため使わない） */}
        {/* FormControl で囲むと配下の入力欄すべてにフォーカス状態が伝播してしまうため Box を使う（#3158） */}
        {!isTemplateMode && pattern && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                mb: 1,
                color: gray100,
                paddingLeft: '12px',
                borderLeft: `4px solid ${orange50}`,
              }}
            >
              出力項目
            </Typography>
            <Typography sx={{ marginBottom: '16px', ...regularMd, color: gray80 }}>
              出力する項目を選択し、必要に応じて出力時の列名を変更してください
            </Typography>

            {
              <Box>
                <Box sx={{ marginBottom: '16px' }}>
                  <Button
                    onClick={handleSelectAll}
                    sx={{
                      marginBottom: '8px',
                      ...tertiaryButtonBackgroundColor,
                      '& .MuiButton-root': {
                        ...tertiaryButtonBackgroundColor,
                      },
                    }}
                    variant="contained"
                    size="small"
                  >
                    <Typography sx={{ ...boldMd, color: gray80 }}>
                      {selectedExportFields.length === getAllSelectableFields().length &&
                      selectedFixedFields.length === allFixedFieldIds.length
                        ? '全解除'
                        : '全選択'}
                    </Typography>
                  </Button>
                </Box>

                {/* 項目リストの列見出し（#3158） */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginBottom: '4px',
                  }}
                >
                  <Typography sx={{ ...regularSm, color: gray80, flex: 1, paddingLeft: '40px' }}>
                    項目
                  </Typography>
                  <Typography sx={{ ...regularSm, color: gray80, width: '240px', flexShrink: 0 }}>
                    出力時の列名
                  </Typography>
                </Box>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={unifiedList.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <FormGroup>
                      {unifiedList.map(item => {
                        if (item.type === 'fixed') {
                          return (
                            <SortableExportItem
                              key={item.id}
                              id={item.id}
                              label={item.label}
                              checked={selectedFixedFields.includes(item.id)}
                              customLabel={customHeaders[item.id] ?? ''}
                              defaultLabel={getDefaultLabel(item)}
                              errorText={customLabelErrors[item.id]}
                              warningText={customLabelWarnings[item.id]}
                              onToggle={handleFixedFieldToggle}
                              onCustomLabelChange={handleCustomLabelChange}
                            />
                          );
                        }
                        return (
                          <SortableExportItem
                            key={item.id}
                            id={item.id}
                            label={
                              item.type === 'child' ? item.child.childValue : item.item.parentValue
                            }
                            checked={selectedExportFields.includes(item.id)}
                            customLabel={customHeaders[item.id] ?? ''}
                            defaultLabel={getDefaultLabel(item)}
                            errorText={customLabelErrors[item.id]}
                            warningText={customLabelWarnings[item.id]}
                            onToggle={handleExportFieldToggle}
                            onCustomLabelChange={handleCustomLabelChange}
                          />
                        );
                      })}
                    </FormGroup>
                  </SortableContext>
                </DndContext>
              </Box>
            }
          </Box>
        )}

        {/* 出力後の処理 */}
        <Box>
          <Typography
            sx={{
              mb: 1,
              color: gray100,
              paddingLeft: '12px',
              borderLeft: `4px solid ${orange50}`,
            }}
          >
            出力後の処理
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px', color: gray100 }}>
              出力したデータを&quot;出力済&quot;タブへ移動する
            </Typography>
            <Switch
              checked={moveToOutputted}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setMoveToOutputted(e.target.checked)
              }
            />
            <Typography sx={{ fontSize: '14px', color: gray80 }}>
              {moveToOutputted ? 'ON' : 'OFF'}
            </Typography>
          </Box>
        </Box>

        {/* 設定保存（指定フォーマットは出力項目・文字コードを使わないため保存対象がない） */}
        {!isTemplateMode && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveSetting}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                    setSaveSetting(e.target.checked)
                  }
                  size="small"
                  sx={{ '&.Mui-checked': { color: orange50 } }}
                />
              }
              label="この設定を保存する"
              sx={{ '& .MuiFormControlLabel-label': { ...regularMd, color: gray80 } }}
            />
          </Box>
        )}

        {/* ファイル名。指定フォーマットは .xlsx 固定で、既定はフォーマット名 */}
        <FileNameWithExtensionField
          value={customFileName}
          onChange={(e): void => {
            setCustomFileName(e.target.value);
            if (!customFileNameTouched) setCustomFileNameTouched(true);
          }}
          onBlur={(): void => setCustomFileNameTouched(true)}
          error={customFileNameTouched && Boolean(customFileNameError)}
          helperText={customFileNameTouched ? customFileNameError : undefined}
          extension={isTemplateMode || fileType === exportFileType.excel ? '.xlsx' : '.csv'}
        />
      </Box>

      {/* ボタン */}
      <Box sx={styles.dialogButtonWrapper}>
        <Button sx={styles.uploadCancelButton} onClick={onClose} disabled={exporting}>
          <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
        </Button>
        <Button
          sx={styles.uploadSubmitButton}
          onClick={isTemplateMode ? (): void => void handleTemplateExport() : handleExport}
          disabled={
            exporting ||
            // 指定フォーマットは出力項目・列名を使わないため、フォーマット選択だけを見る
            (isTemplateMode
              ? selectedTemplateId === '' || Boolean(customFileNameError)
              : (selectedExportFields.length === 0 && selectedFixedFields.length === 0) ||
                hasCustomLabelError ||
                Boolean(customFileNameError))
          }
        >
          {exporting ? (
            <CircularProgress size={20} sx={{ color: white }} />
          ) : (
            <Typography sx={styles.uploadSubmitButtonText}>出力する</Typography>
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default FileExportDialog;
